import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["banner", "modal"];

  connect() {
    this.handleEscapeBound = this.handleEscape.bind(this);
    this.handleOutsideClickBound = this.handleOutsideClick.bind(this);

    // Check for valid consent
    try {
      const consent = localStorage.getItem("cookieConsent");
      if (consent) {
        const preferences = JSON.parse(consent);
        if (preferences && typeof preferences === "object") {
          document.documentElement.classList.remove("show-cookie-consent");
          return;
        }
      }
      document.documentElement.classList.add("show-cookie-consent");
    } catch (e) {
      console.error("Error parsing cookie consent:", e);
      localStorage.removeItem("cookieConsent");
      document.documentElement.classList.add("show-cookie-consent");
    }
  }

  disconnect() {
    this.removeEventListeners();
  }

  customize(event) {
    event.preventDefault();
    event.stopPropagation();
    this.modalTarget.classList.remove("hidden");
    document.body.classList.add("overflow-hidden");
    this.addEventListeners();
    this.loadSavedPreferences();
  }

  closeModal(event) {
    event.preventDefault();
    event.stopPropagation();
    this.modalTarget.classList.add("hidden");
    document.body.classList.remove("overflow-hidden");
    this.removeEventListeners();
  }

  addEventListeners() {
    document.addEventListener("keydown", this.handleEscapeBound);
    document.addEventListener("click", this.handleOutsideClickBound);
  }

  removeEventListeners() {
    document.removeEventListener("keydown", this.handleEscapeBound);
    document.removeEventListener("click", this.handleOutsideClickBound);
  }

  handleEscape(event) {
    if (event.key === "Escape" && !this.modalTarget.classList.contains("hidden")) {
      this.closeModal(event);
    }
  }

  handleOutsideClick(event) {
    if (!this.modalTarget.classList.contains("hidden") && event.target === this.modalTarget) {
      this.closeModal(event);
    }
  }

  acceptAll(event) {
    event.preventDefault();
    event.stopPropagation();
    this.setConsent({
      essential: true,
      analytics: true,
      marketing: true
    });
    document.documentElement.classList.remove("show-cookie-consent");
  }

  toggleAnalytics(event) {
    event.stopPropagation();
  }

  toggleMarketing(event) {
    event.stopPropagation();
  }

  savePreferences(event) {
    event.preventDefault();
    event.stopPropagation();
    const analytics = document.getElementById("analytics-cookie").checked;
    const marketing = document.getElementById("marketing-cookie").checked;

    this.setConsent({
      essential: true,
      analytics: analytics,
      marketing: marketing
    });

    this.closeModal(event);
    document.documentElement.classList.remove("show-cookie-consent");
  }

  hasConsent() {
    try {
      const consent = localStorage.getItem("cookieConsent");
      if (!consent) return false;

      const preferences = JSON.parse(consent);
      return preferences && typeof preferences === "object";
    } catch (e) {
      console.error("Error checking cookie consent:", e);
      localStorage.removeItem("cookieConsent");
      return false;
    }
  }

  setConsent(preferences) {
    try {
      localStorage.setItem("cookieConsent", JSON.stringify(preferences));

      if (preferences.analytics) {
        this.enableAnalytics();
      } else {
        this.disableAnalytics();
      }

      if (preferences.marketing) {
        this.enableMarketing();
      } else {
        this.disableMarketing();
      }
    } catch (e) {
      console.error("Error setting cookie consent:", e);
    }
  }

  loadSavedPreferences() {
    try {
      const preferences = JSON.parse(localStorage.getItem("cookieConsent") || "{}");

      const analyticsCheckbox = document.getElementById("analytics-cookie");
      const marketingCheckbox = document.getElementById("marketing-cookie");

      if (analyticsCheckbox) {
        analyticsCheckbox.checked = preferences.analytics !== false; // Default to true
      }
      if (marketingCheckbox) {
        marketingCheckbox.checked = preferences.marketing !== false; // Default to true
      }
    } catch (e) {
      console.error("Error loading saved preferences:", e);
    }
  }

  enableAnalytics() {
    // Initialize analytics
    if (window.gtag) {
      gtag("consent", "update", {
        analytics_storage: "granted"
      });
    }
  }

  disableAnalytics() {
    // Disable analytics
    if (window.gtag) {
      gtag("consent", "update", {
        analytics_storage: "denied"
      });
    }
  }

  enableMarketing() {
    // Initialize marketing
    if (window.gtag) {
      gtag("consent", "update", {
        ad_storage: "granted"
      });
    }
  }

  disableMarketing() {
    // Disable marketing
    if (window.gtag) {
      gtag("consent", "update", {
        ad_storage: "denied"
      });
    }
  }
}
